import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

/** Single-flight refresh so concurrent 401s share one refresh call. */
let refreshPromise = null;

/** Skip back-to-back refreshes right after a successful one (Strict Mode / race). */
let lastRefreshOkAt = 0;
const REFRESH_SUCCESS_COOLDOWN_MS = 3_000;

/** True only while AuthContext has a logged-in user (avoids guest bootstrap toasts). */
let sessionActive = false;

/**
 * Once session is known-dead, stop refresh retries so the UI doesn't lag on
 * repeated 401 → refresh → fail loops behind the Session expired modal.
 */
let sessionFrozen = false;

const REFRESH_LOCK_KEY = "vbot-auth-refresh-lock";
const REFRESH_LOCK_TTL_MS = 20_000;

export function setAuthSessionActive(active) {
  sessionActive = !!active;
  if (active) {
    sessionFrozen = false;
  }
}

export function getAuthSessionActive() {
  return sessionActive;
}

/** Freeze auth recovery (session expired). Call before showing the modal. */
export function setAuthSessionFrozen(frozen) {
  sessionFrozen = !!frozen;
  if (frozen) {
    sessionActive = false;
    refreshPromise = null;
    lastRefreshOkAt = 0;
    try {
      localStorage.removeItem(REFRESH_LOCK_KEY);
    } catch {
      /* ignore */
    }
  }
}

export function getAuthSessionFrozen() {
  return sessionFrozen;
}

const AUTH_SKIP_RE =
  /\/auth\/(login|signup|logout|refresh-token|password-reset|otp|google|verify-email)/i;

function shouldSkipRefresh(config) {
  if (!config) return true;
  if (config._retry) return true;
  if (config.skipAuthRefresh) return true;
  const url = `${config.baseURL || ""}${config.url || ""}`;
  return AUTH_SKIP_RE.test(url);
}

function getErrorStatus(error) {
  return (
    error?.response?.status ??
    error?.status ??
    error?.data?.statusCode ??
    null
  );
}

function getErrorMessage(error) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.body?.message ||
    error?.data?.message ||
    error?.data?.body?.message ||
    error?.message ||
    ""
  );
}

/**
 * True when the failure is likely an expired/missing access token
 * (not a business 403 like subscription limits).
 */
export function isAuthTokenError(error) {
  const status = getErrorStatus(error);
  const message = String(getErrorMessage(error));

  if (status === 401) return true;
  if (status === 403) {
    return /invalid token|no token|access denied|expired or revoked|unauthorized/i.test(
      message
    );
  }
  return false;
}

function emitSessionExpired() {
  if (sessionFrozen || !sessionActive || typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("auth:session-expired"));
}

function readLock() {
  try {
    const raw = localStorage.getItem(REFRESH_LOCK_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function tryAcquireCrossTabLock() {
  if (typeof localStorage === "undefined") return true;
  const now = Date.now();
  const lock = readLock();
  if (lock && now - (lock.ts || 0) < REFRESH_LOCK_TTL_MS) {
    return false;
  }
  const id = `${now}-${Math.random().toString(36).slice(2, 8)}`;
  try {
    localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify({ id, ts: now }));
    const confirm = readLock();
    return confirm?.id === id;
  } catch {
    return true;
  }
}

function releaseCrossTabLock() {
  try {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  } catch {
    /* ignore */
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Refresh access token via cookies. Uses a bare axios call (not an intercepted
 * client) so refresh never recurses into this interceptor.
 *
 * Resolves true on success. Rejects with `{ networkError, status }` on failure.
 */
export function refreshAccessToken() {
  if (sessionFrozen) {
    const err = new Error("Session frozen");
    err.status = 401;
    err.networkError = false;
    return Promise.reject(err);
  }

  // Recent success: cookies should already be fresh — avoid rotating twice.
  if (
    !refreshPromise &&
    lastRefreshOkAt &&
    Date.now() - lastRefreshOkAt < REFRESH_SUCCESS_COOLDOWN_MS
  ) {
    return Promise.resolve(true);
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      // If another tab is already refreshing, wait briefly then assume cookies updated.
      if (!tryAcquireCrossTabLock()) {
        await sleep(1200);
        lastRefreshOkAt = Date.now();
        return true;
      }

      try {
        const response = await axios.get(`${API_URL}/auth/refresh-token`, {
          withCredentials: true,
          // Mark so any future wrapping still skips retry loops
          skipAuthRefresh: true,
        });
        if (response.data?.statusCode === 200) {
          lastRefreshOkAt = Date.now();
          return true;
        }
        const err = new Error("Refresh failed");
        err.status = response.data?.statusCode ?? response.status;
        err.networkError = false;
        throw err;
      } catch (error) {
        const status = getErrorStatus(error);
        const networkError = !error?.response;
        const err = new Error(getErrorMessage(error) || "Refresh failed");
        err.status = status;
        err.networkError = networkError;
        throw err;
      } finally {
        releaseCrossTabLock();
      }
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Attach 401/invalid-token → refresh → retry once to an axios instance.
 */
export function attachAuthRefreshInterceptor(instance) {
  if (!instance || instance.__authRefreshAttached) return instance;
  instance.__authRefreshAttached = true;

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config;

      // Session already expired — fail fast, no more refresh attempts.
      if (sessionFrozen) {
        return Promise.reject(error);
      }

      if (!config || shouldSkipRefresh(config) || !isAuthTokenError(error)) {
        return Promise.reject(error);
      }

      try {
        await refreshAccessToken();
        config._retry = true;
        return instance.request(config);
      } catch (refreshError) {
        // Network blip while refreshing: keep session; let the caller retry later.
        if (refreshError?.networkError) {
          return Promise.reject(error);
        }
        emitSessionExpired();
        return Promise.reject(error);
      }
    }
  );

  return instance;
}
